import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Activity, Check } from 'lucide-react';
import { useAdvisorAssessments, useSendAssessment } from '../../hooks/useLongevityAssessments';
import { SelfTestRuns, TrySelfTestButton } from './SelfTestRuns';
import { CATEGORY_LABELS } from './categoryLabels';
import type { AdvisorAssessments, AssessmentCard } from '../../api/longevityAssessmentApi';

function statusLabel(card: AssessmentCard): string {
  if (card.status === 'COMPLETED') return card.isPrivate ? 'Completed · Private' : 'Completed';
  if (card.status === 'IN_PROGRESS') {
    // Six pillars in V1, five in MVP 2 — the count comes from the card so the
    // denominator matches the assessment the client is actually taking.
    return `In progress (${card.pillarsAnswered ?? 0} of ${card.pillarCount ?? 6} pillars)`;
  }
  return 'Invited';
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="font-serif text-2xl text-gray-900">{value}</div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}

/**
 * Resend the invitation email for a not-yet-completed assessment. Reuses the send
 * endpoint (it resends the existing in-flight link rather than minting a new one);
 * a 429 means the 30-minute cooldown hasn't elapsed.
 */
function ResendButton({ card }: { card: AssessmentCard }) {
  const sendAssessment = useSendAssessment();
  const [resent, setResent] = useState(false);

  if (resent) {
    return (
      <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#026857]">
        <Check className="h-3.5 w-3.5" /> Resent
      </span>
    );
  }

  const isCooldown =
    (sendAssessment.error as { response?: { status?: number } } | null)?.response?.status === 429;

  // The version has to come along: the backend looks for the in-flight
  // assessment by advisor + email + version, so resending without it would
  // miss this card's row and mint a second assessment on the other version.
  const handleResend = () =>
    sendAssessment.mutate(
      {
        clientName: card.clientName,
        clientEmail: card.clientEmail,
        deliveryMode: 'EMAIL',
        questionSetVersion: card.questionSetVersion,
      },
      {
        onSuccess: () => {
          setResent(true);
          setTimeout(() => setResent(false), 3000);
        },
      },
    );

  return (
    <div className="mt-2 flex flex-col items-end gap-1">
      <Button variant="outline" size="sm" onClick={handleResend} disabled={sendAssessment.isPending}>
        {sendAssessment.isPending ? 'Resending…' : 'Resend'}
      </Button>
      {isCooldown && (
        <span className="max-w-[11rem] text-[11px] text-amber-600">
          Contacted recently — please wait before resending.
        </span>
      )}
    </div>
  );
}

function AssessmentClientCard({ card }: { card: AssessmentCard }) {
  const navigate = useNavigate();
  // Score is present only for shared completed results — the backend omits it for
  // private ones, so there is nothing to leak here.
  const showScore = card.status === 'COMPLETED' && !card.isPrivate && card.overallScore != null;

  const reportPath =
    card.questionSetVersion === '2'
      ? `/longevity-assessment/advisor-report/${card.id}`
      : `/longevity-assessment/results/${card.id}`;

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
      <div>
        <div className="font-medium text-gray-900">{card.clientName}</div>
        <div className="text-sm text-gray-500">{card.clientEmail}</div>
        <Badge variant="secondary" className="mt-1">
          {statusLabel(card)}
        </Badge>
      </div>
      <div className="text-right">
        {showScore ? (
          <>
            <div className="font-serif text-2xl text-[#026857]">{card.overallScore}</div>
            {card.category && (
              <div className="text-xs text-gray-500">{CATEGORY_LABELS[card.category]}</div>
            )}
            {/* MVP 2 has an advisor view — what was learned, what to ask, what
                to do — which V1 has no content for. So the button goes where
                there is something to open, rather than offering both. */}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(reportPath)}>
              View report
            </Button>
          </>
        ) : card.status === 'COMPLETED' ? (
          <div className="text-sm text-gray-400">Results not shared</div>
        ) : (
          <>
            <div className="text-sm text-gray-400">Awaiting results</div>
            <ResendButton card={card} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The advisor's Longevity Assessments section (Feature 095, Slice 1).
 * Average readiness reflects shared results only; private results never expose a score.
 *
 * A card of its own beside My Clients rather than a block inside it. It was
 * nested there, which read as positioning until Feature 118 put the advisor's own
 * test runs in it: the clients card returns its loading and error states instead
 * of its body, so a failure of the *client* query stopped an advisor starting a
 * test run that has nothing to do with clients. It has always had its own query.
 *
 * The stats and client cards cover clients only. The advisor's own test runs
 * (Feature 118) are listed separately below them, by SelfTestRuns — counting
 * them here would have let an advisor move their own headline numbers.
 */
/**
 * The client half: the stat strip and one card each.
 *
 * Its loading and error states live here rather than in the section, which used
 * to return them instead of rendering at all — and took the advisor's own test
 * runs down with it. The two halves answer different endpoints and neither
 * should decide whether the other appears.
 */
function ClientAssessments({
  data,
  isLoading,
  isError,
}: {
  data: AdvisorAssessments | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) return <p className="py-2 text-center text-gray-500">Loading assessments...</p>;
  if (isError || !data) {
    return (
      <p className="py-2 text-center text-red-600">
        Error loading assessments. Please try again later.
      </p>
    );
  }
  /*
   * The stat strip and client list wait for a real send — three zeroes and an
   * empty list say less than nothing. The heading and the test runs do not: a
   * test run is deliberately not counted in `sent` (Feature 118), so gating the
   * whole section on it hid the test-run button from the advisor who most
   * wants it.
   */
  if (data.stats.sent === 0) return null;

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Sent" value={data.stats.sent} />
        <StatTile label="Completed" value={data.stats.completed} />
        <StatTile
          label="Avg. readiness"
          value={data.stats.averageReadiness == null ? '—' : data.stats.averageReadiness}
        />
      </div>

      <div className="space-y-2">
        {data.clients.map((card) => (
          <AssessmentClientCard key={card.id} card={card} />
        ))}
      </div>
    </>
  );
}

export function LongevityAssessmentsSection() {
  const { data, isLoading, isError } = useAdvisorAssessments();

  return (
    <Card className="mt-6 space-y-4 p-6">
      {/* The start button lives up here, the first thing in the section, rather
          than under the advisor's list of runs. */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#026857]" />
          <h4 className="text-gray-900">Longevity Assessments</h4>
        </div>
        <TrySelfTestButton />
      </div>

      <ClientAssessments data={data} isLoading={isLoading} isError={isError} />

      {/*
        Outside the branch above, not inside it. The client query failing is not
        a reason an advisor cannot start or see their own test runs — those come
        from a different endpoint, and SelfTestRuns handles its own failure while
        the start button above stays — which it never got to do while a sibling
        query's error returned in place of this whole section.
      */}
      <SelfTestRuns />
    </Card>
  );
}
